import {
  Shield,
  FileSearch,
  Lock,
  AlertTriangle,
  Users,
  BarChart3,
  HeartPulse,
  Brain,
  Boxes,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface ServiceCard {
  /** URL slug — the card links to /services/{id} */
  id: string;
  path: string;
  icon: LucideIcon;
  title: string;
  /** Short summary used on the card and as the hero subtitle. */
  description: string;
  /** Background image, shared by the card and the detail page hero. */
  image: string;
  /** Alt / descriptive text for the background image. */
  imageAlt: string;
}

export const serviceCards: ServiceCard[] = [
  {
    id: 'security-strategy',
    path: '/services/security-strategy',
    icon: Shield,
    title: 'Security Strategy',
    description:
      'Develop comprehensive cybersecurity roadmaps aligned with your business objectives and risk appetite.',
    image: '/images/services/security-strategy.avif',
    imageAlt: 'Strategic planning session mapping a security roadmap',
  },
  {
    id: 'risk-assessment',
    path: '/services/risk-assessment',
    icon: FileSearch,
    title: 'Risk Assessment',
    description:
      'Identify vulnerabilities and threats across your digital infrastructure with thorough risk analysis.',
    image: '/images/services/risk-assessment.avif',
    imageAlt: 'Analyst reviewing risk and vulnerability data',
  },
  {
    id: 'compliance',
    path: '/services/compliance',
    icon: Lock,
    title: 'Compliance & Governance',
    description:
      'Navigate complex regulatory landscapes including GDPR, SOC 2, ISO 27001, and industry-specific standards.',
    image: '/images/services/compliance.avif',
    imageAlt: 'Compliance documentation and governance controls',
  },
  {
    id: 'incident-response',
    path: '/services/incident-response',
    icon: AlertTriangle,
    title: 'Incident Response',
    description:
      'Build and test incident response plans so your team is prepared when breaches occur.',
    image: '/images/services/incident-response.avif',
    imageAlt: 'Security operations team responding to an incident',
  },
  {
    id: 'security-culture',
    path: '/services/security-culture',
    icon: Users,
    title: 'Security Culture',
    description:
      'Train your workforce to become your strongest defence through awareness programmes and phishing simulations.',
    image: '/images/services/security-culture.avif',
    imageAlt: 'Team engaged in a security awareness training session',
  },
  {
    id: 'board-reporting',
    path: '/services/board-reporting',
    icon: BarChart3,
    title: 'Board Reporting',
    description:
      'Translate technical risks into business language for board-level cyber security reporting.',
    image: '/images/services/board-reporting.avif',
    imageAlt: 'Executive presenting cyber risk metrics to a board',
  },
  {
    id: 'cyber-resilience',
    path: '/services/cyber-resilience',
    icon: HeartPulse,
    title: 'Cyber Resilience',
    description:
      'Keep operating through disruption with business continuity, disaster recovery, and operational resilience planning.',
    image: '/images/services/cyber-resilience.avif',
    imageAlt: 'Resilient data infrastructure keeping operations running',
  },
  {
    id: 'ai-governance',
    path: '/services/ai-governance',
    icon: Brain,
    title: 'AI Governance',
    description:
      'Govern AI adoption with confidence — policies, controls, and oversight that keep innovation safe, compliant, and auditable.',
    image: '/images/services/ai-governance.avif',
    imageAlt: 'AI governance and oversight controls',
  },
  {
    id: 'supply-chain-risk',
    path: '/services/supply-chain-risk',
    icon: Boxes,
    title: 'Supply Chain Risk',
    description:
      'Manage the risk your vendors and third parties introduce, with assessment, due diligence, and continuous monitoring across your supply chain.',
    image: '/images/services/supply-chain-risk.avif',
    imageAlt: 'Global supply chain and third-party vendor network',
  },
];

export const getServiceCard = (id: string): ServiceCard | undefined =>
  serviceCards.find(service => service.id === id);
